import { ContractEvent } from "./types.js";

function formatEventAction(action: string) {
  return action.replace(/_/g, " ");
}

export function ContractEventsTimeline({ events }: { events: ContractEvent[] }) {
  if (events.length === 0) {
    return <p className="text-xs text-content-muted">No contract events recorded.</p>;
  }

  return (
    <ol className="space-y-2 border-l border-border-subtle pl-3">
      {events.map((event) => (
        <li key={event.id} className="text-xs text-content-secondary">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="font-medium text-content-primary capitalize">{formatEventAction(event.action)}</span>
            {event.fromStatus && event.toStatus && (
              <span>
                {event.fromStatus} → {event.toStatus}
              </span>
            )}
            {!event.fromStatus && event.toStatus && <span>→ {event.toStatus}</span>}
            <span className="text-content-muted">{new Date(event.createdAt).toLocaleString()}</span>
          </div>
          {event.notes && <p className="mt-0.5 text-content-muted">{event.notes}</p>}
          {event.actorUser && (
            <p className="mt-0.5 text-content-muted">By {event.actorUser.email}</p>
          )}
        </li>
      ))}
    </ol>
  );
}

export function ContractUpstreamPanel({
  contract,
}: {
  contract: {
    upstreamStatusName?: string | null;
    upstreamSource?: string | null;
    upstreamExternalId?: string | null;
    upstreamSyncedAt?: string | null;
    upstreamUplineName?: string | null;
    upstreamUplineNpn?: string | null;
    lastReconciledAt?: string | null;
    pinnedAt?: string | null;
    pinnedReason?: string | null;
    pinnedByUser?: { email: string } | null;
  };
}) {
  const hasUpstream =
    contract.upstreamStatusName ||
    contract.upstreamSource ||
    contract.upstreamSyncedAt ||
    contract.upstreamUplineName ||
    contract.upstreamUplineNpn ||
    contract.lastReconciledAt;
  const isPinned = Boolean(contract.pinnedAt);

  if (!hasUpstream && !isPinned) return null;

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-base p-3 space-y-2 text-xs">
      {hasUpstream && (
        <dl className="grid gap-2 sm:grid-cols-2">
          {contract.upstreamStatusName && (
            <div>
              <dt className="text-content-muted uppercase tracking-wider">Upstream status</dt>
              <dd className="text-content-primary">{contract.upstreamStatusName}</dd>
            </div>
          )}
          {(contract.upstreamUplineName || contract.upstreamUplineNpn) && (
            <div>
              <dt className="text-content-muted uppercase tracking-wider">Upstream upline</dt>
              <dd className="text-content-primary">
                {contract.upstreamUplineName || "—"}
                {contract.upstreamUplineNpn ? ` · NPN ${contract.upstreamUplineNpn}` : ""}
              </dd>
            </div>
          )}
          {contract.upstreamSource && (
            <div>
              <dt className="text-content-muted uppercase tracking-wider">Source</dt>
              <dd className="text-content-primary">{contract.upstreamSource}</dd>
            </div>
          )}
          {contract.upstreamExternalId && (
            <div>
              <dt className="text-content-muted uppercase tracking-wider">External ID</dt>
              <dd className="text-content-primary font-mono">{contract.upstreamExternalId}</dd>
            </div>
          )}
          {contract.upstreamSyncedAt && (
            <div>
              <dt className="text-content-muted uppercase tracking-wider">Upstream synced</dt>
              <dd className="text-content-primary">{new Date(contract.upstreamSyncedAt).toLocaleString()}</dd>
            </div>
          )}
          {contract.lastReconciledAt && (
            <div>
              <dt className="text-content-muted uppercase tracking-wider">Last reconciled</dt>
              <dd className="text-content-primary">{new Date(contract.lastReconciledAt).toLocaleString()}</dd>
            </div>
          )}
        </dl>
      )}
      {isPinned && (
        <p className="text-content-secondary">
          Pinned{contract.pinnedByUser ? ` by ${contract.pinnedByUser.email}` : ""}
          {contract.pinnedAt ? ` at ${new Date(contract.pinnedAt).toLocaleString()}` : ""}
          {contract.pinnedReason ? ` — ${contract.pinnedReason}` : ""}
        </p>
      )}
    </div>
  );
}
